import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import type { MarketingCampaign } from '../../services/marketingApi';

interface CampaignModalProps {
  campaign: MarketingCampaign;
  onClose: () => void;
  onDoNotShowAgain: () => void;
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  max-width: 90%;
  max-height: 80%;
  overflow: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 2001;
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;
  color: #374151;
  z-index: 10;
  
  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const ContentContainer = styled.div`
  padding: 24px;
  flex: 1;
  overflow-y: auto;
`;

const TextContent = styled.div`
  font-size: 16px;
  line-height: 1.6;
  color: #374151;
  white-space: pre-wrap;
  
  /* Style HTML content */
  a {
    color: #2563eb;
    text-decoration: underline;
    cursor: pointer;
    
    &:hover {
      color: #1d4ed8;
      text-decoration: none;
    }
    
    &:visited {
      color: #7c3aed;
    }
  }
  
  p {
    margin: 8px 0;
    
    &:first-child {
      margin-top: 0;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong, b {
    font-weight: 600;
  }
  
  em, i {
    font-style: italic;
  }
  
  ul, ol {
    margin: 8px 0;
    padding-left: 24px;
  }
  
  li {
    margin: 4px 0;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  display: block;
  max-height: 500px;
  object-fit: contain;
  border-radius: 8px;
`;

const VideoContainer = styled.div`
  width: 100%;
  margin-bottom: 16px;
  position: relative;
`;

const Video = styled.video`
  width: 100%;
  height: auto;
  display: block;
  max-height: 500px;
  border-radius: 8px;
`;

const MessageText = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
  white-space: pre-wrap;
  margin-top: 16px;
  
  /* Style HTML content */
  a {
    color: #2563eb;
    text-decoration: underline;
    cursor: pointer;
    
    &:hover {
      color: #1d4ed8;
      text-decoration: none;
    }
    
    &:visited {
      color: #7c3aed;
    }
  }
  
  p {
    margin: 8px 0;
    
    &:first-child {
      margin-top: 0;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong, b {
    font-weight: 600;
  }
  
  em, i {
    font-style: italic;
  }
  
  ul, ol {
    margin: 8px 0;
    padding-left: 24px;
  }
  
  li {
    margin: 4px 0;
  }
`;

const LoadingContainer = styled.div`
  width: 100%;
  min-height: 300px;
  background-color: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
`;

const FooterContainer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: center;
  gap: 12px;
`;

const DoNotShowAgainButton = styled.button`
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
    color: #374151;
  }
  
  &:active {
    background: #f3f4f6;
  }
`;

export const CampaignModal: React.FC<CampaignModalProps> = ({ campaign, onClose, onDoNotShowAgain }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Helper function to render HTML content safely
  const renderHtmlContent = (html: string | null | undefined, Component: typeof TextContent | typeof MessageText) => {
    if (!html) return null;
    
    // Check if content contains HTML tags
    const hasHtmlTags = /<[^>]+>/.test(html);
    
    if (hasHtmlTags) {
      // Render as HTML
      return <Component dangerouslySetInnerHTML={{ __html: html }} />;
    } else {
      // Render as plain text with line breaks
      return <Component>{html}</Component>;
    }
  };

  const renderContent = () => {
    if (campaign.communication_type === 'modal' && campaign.content_type === 'text') {
      return (
        <ContentContainer>
          {renderHtmlContent(campaign.message || 'No message available', TextContent)}
        </ContentContainer>
      );
    }

    if (campaign.communication_type === 'modal' && campaign.content_type === 'image') {
      return (
        <ContentContainer>
          {campaign.image_url ? (
            <>
              {!imageLoaded && !imageError && (
                <LoadingContainer>Loading image...</LoadingContainer>
              )}
              {imageError && (
                <LoadingContainer>Failed to load image</LoadingContainer>
              )}
              <ImageContainer style={{ display: imageLoaded ? 'block' : 'none' }}>
                <Image
                  src={campaign.image_url}
                  alt="Campaign"
                  onLoad={() => setImageLoaded(true)}
                  onError={() => {
                    setImageError(true);
                    setImageLoaded(false);
                  }}
                />
              </ImageContainer>
              {campaign.message && renderHtmlContent(campaign.message, MessageText)}
            </>
          ) : (
            <LoadingContainer>No image available</LoadingContainer>
          )}
        </ContentContainer>
      );
    }

    if (campaign.communication_type === 'modal' && campaign.content_type === 'video') {
      return (
        <ContentContainer>
          {campaign.video_url ? (
            <>
              {!videoLoaded && !videoError && (
                <LoadingContainer>Loading video...</LoadingContainer>
              )}
              {videoError && (
                <LoadingContainer>Failed to load video</LoadingContainer>
              )}
              <VideoContainer style={{ display: videoLoaded ? 'block' : 'none' }}>
                <Video
                  src={campaign.video_url}
                  controls
                  onLoadedData={() => setVideoLoaded(true)}
                  onError={() => {
                    setVideoError(true);
                    setVideoLoaded(false);
                  }}
                />
              </VideoContainer>
              {campaign.message && renderHtmlContent(campaign.message, MessageText)}
            </>
          ) : (
            <LoadingContainer>No video available</LoadingContainer>
          )}
        </ContentContainer>
      );
    }

    return (
      <ContentContainer>
        <TextContent>{campaign.message || 'No content available'}</TextContent>
      </ContentContainer>
    );
  };

  return (
    <Overlay onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="campaign-modal-title">
      <ModalContainer>
        <CloseButton onClick={onClose} aria-label="Close modal" type="button">
          ×
        </CloseButton>
        {renderContent()}
        <FooterContainer>
          <DoNotShowAgainButton onClick={onDoNotShowAgain} type="button">
            Do not show again
          </DoNotShowAgainButton>
        </FooterContainer>
      </ModalContainer>
    </Overlay>
  );
};
